<!--
  SeloRegulatorio — selo de credibilidade regulatória (ex: ANVISA, INVIMA).
  Emblema circular tipo carimbo (anéis + check + sigla da agência, tudo
  desenhado em SVG para centralizar sem overflow) ao lado de um bloco de
  texto. Usado nas landings por país para reforçar conformidade regulatória.

  Props:
    - agencia   : 'anvisa' | 'invima'  (define a sigla exibida e o acento de cor)
    - logoSrc   : caminho do logo oficial da agência. Quando informado, exibe a
                  imagem num chip branco no lugar do emblema SVG desenhado.
    - logoAlt   : alt da imagem do logo
    - etiqueta  : texto pequeno em caixa alta (ex: 'Conformidade regulatória')
    - titulo    : destaque (ex: 'Registrado na ANVISA')
    - descricao : linha de apoio explicativa (opcional)
-->
<template>
  <div :class="['selo', `selo--${agencia}`]">
    <div v-if="logoSrc" class="selo-logo">
      <img :src="logoSrc" :alt="logoAlt || titulo" loading="lazy" />
    </div>

    <svg v-else class="selo-emblema" viewBox="0 0 72 72" width="60" height="60" aria-hidden="true">
      <circle class="selo-ring selo-ring--out" cx="36" cy="36" r="33" />
      <circle class="selo-ring selo-ring--in" cx="36" cy="36" r="27" />
      <path
        class="selo-check"
        d="M28 28.5l5 5 11-11"
        fill="none"
        stroke-width="3.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <text class="selo-sigla" x="36" y="49" text-anchor="middle">{{ sigla }}</text>
    </svg>

    <div class="selo-texto">
      <span v-if="etiqueta" class="selo-etiqueta">{{ etiqueta }}</span>
      <strong class="selo-titulo">{{ titulo }}</strong>
      <span v-if="descricao" class="selo-desc">{{ descricao }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  agencia: {
    type: String,
    required: true,
    validator: (v) => ['anvisa', 'invima'].includes(v)
  },
  logoSrc: { type: String, default: '' },
  logoAlt: { type: String, default: '' },
  etiqueta: { type: String, default: '' },
  titulo: { type: String, required: true },
  descricao: { type: String, default: '' }
})

const sigla = computed(() => props.agencia.toUpperCase())
</script>

<style scoped>
.selo {
  display: inline-flex;
  align-items: center;
  gap: 16px;
  text-align: left;
  padding: 16px 22px;
  border-radius: var(--ds-r-lg);
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-border);
  max-width: 460px;
  position: relative;
  overflow: hidden;
}

.selo::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0.1;
  pointer-events: none;
  background: radial-gradient(circle at 12% 50%, var(--selo-cor, var(--ds-success)) 0%, transparent 60%);
}

/* Acento por agência */
.selo--anvisa { --selo-cor: #1aaf95; }
.selo--invima { --selo-cor: #14cbf0; }

.selo-emblema {
  flex-shrink: 0;
  display: block;
}

/* Chip branco quadrado para o logo oficial (logos costumam ter texto
   escuro, ilegível sobre o card escuro). Formato fixo igual para todas
   as agências — logos horizontais (ex: INVIMA) ficam contidos via
   object-fit sem distorcer.
   `position: relative; z-index: 1` garante que o chip fique acima do
   gradiente `::before` do card (senão o branco aparece tingido de escuro). */
.selo-logo {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  width: 72px;
  height: 72px;
  padding: 8px;
  border-radius: var(--ds-r-md);
  background: #fff;
  box-shadow: 0 4px 14px -4px rgba(0, 0, 0, 0.4);
}

.selo-logo img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}

.selo-ring {
  fill: none;
  stroke: var(--selo-cor, var(--ds-success));
}

.selo-ring--out {
  stroke-width: 1.5;
  opacity: 0.4;
  stroke-dasharray: 3 4;
}

.selo-ring--in {
  stroke-width: 2;
  opacity: 0.85;
}

.selo-check {
  stroke: var(--selo-cor, var(--ds-success));
}

.selo-sigla {
  fill: var(--selo-cor, var(--ds-success));
  font-family: var(--ds-font-bold);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.selo-texto {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  position: relative;
  z-index: 1;
}

.selo-etiqueta {
  font-family: var(--ds-font-bold);
  font-size: 10.5px;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: var(--ds-text-dim);
}

.selo-titulo {
  font-family: var(--ds-font-bold);
  font-size: 16px;
  line-height: 1.2;
  color: var(--ds-text);
}

.selo-desc {
  font-size: 12.5px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.62);
}

@media (max-width: 480px) {
  .selo { gap: 12px; padding: 14px 16px; }
  .selo-logo { width: 60px; height: 60px; padding: 6px; }
  .selo-titulo { font-size: 15px; }
  .selo-desc { font-size: 12px; }
}
</style>
